import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { totalDriverIcon, activeDriverIcon, completedJobsIcon, stopSignIcon, addressIcon, scheduleIcon } from '../assets';
import bookingsEmptyIcon from '../assets/images/EmptyIcons/Bookings-Not-Found.svg';
import { bookingAPI } from '../services/api';
import "../css/Bookings.css";
import { ShimmerBox, ShimmerStatCards } from "../components/Shimmer";
import "../css/Shimmer.css";

const Bookings = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("All");
  const [sortBy, setSortBy] = useState("Newest");
  const [bookings, setBookings] = useState([]);
  const [statistics, setStatistics] = useState({
    TotalBookings: 0,
    PendingBookings: 0,
    CompletedBookings: 0,
    CancelledBookings: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  const statusClass = {
    New: "status-new",
    Completed: "status-complete",
    Ongoing: "status-ongoing",
    Cancelled: "status-cancelled",
  };

  // Map sort options to API params
  const sortByMap = {
    "Newest": "newest",
    "Oldest": "oldest",
    "Amount (Low)": "lowest_amount",
    "Amount (High)": "highest_amount",
  };

  // Fetch bookings from API
  const fetchBookings = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      // Build params
      const params = {
        sort_by: sortByMap[sortBy] || 'newest',
        page: page
      };

      // Add status filter (if not "All")
      if (filter !== "All") {
        params.status = filter.toLowerCase();
      }

      const response = await bookingAPI.getBookings(params);
      const data = response.data;

      if (data.Status === 'Success') {
        setStatistics(data.Statistics);
        setBookings(data.Bookings || []);
        setPagination(data.Pagination || null);
      } else {
        throw new Error(data.Message || 'Failed to fetch bookings');
      }
    } catch (err) {
      setError(err.response?.data?.Message || err.message || 'Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch bookings when filter or sort changes
  useEffect(() => {
    setCurrentPage(1);
    fetchBookings(1);
  }, [filter, sortBy]);

  // Fetch when page changes
  useEffect(() => {
    if (currentPage > 1) {
      fetchBookings(currentPage);
    }
  }, [currentPage]);

  // Handle View Details click
  const handleViewDetails = (booking) => {
    // Navigate to booking details page with booking ID
    navigate(`/booking-details/${booking.BookingId}`);
  };

  // Format amount
  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '$0';
    return `$${Number(amount).toLocaleString()}`;
  };

  // Render rating stars
  // Render rating stars
const renderRating = (rating) => {
  // If no rating, show "Not Rated Yet"
  if (rating === null || rating === undefined || rating === 0) {
    return <span className="no-rating">Not Rated Yet</span>;
  }

  // Show filled stars based on rating
  return (
    <span className="rating-stars">
      {Array.from({ length: 5 }).map((_, idx) => (
        <span
          key={idx}
          className={`star ${idx < rating ? "filled" : ""}`}
        >
          ★
        </span>
      ))}
    </span>
  );
};
  return (
    <div className="bookings-page">
      <div className="container-fluid px-4 py-4">

        {/* Top Stats Cards */}
        <div className="stats-row">
          <div className="stat-col">
            <div className="stats-card" style={{ borderLeft: '4px solid #8362E5' }}>
              <div className="stat-content">
                <div className="stat-info">
                  <p className="stat-title">Total Bookings</p>
                  <h2 className="stat-value">{statistics.TotalBookings}</h2>
                </div>
                <div className="stat-icon">
                  <img src={totalDriverIcon} alt="Total Bookings" />
                </div>
              </div>
            </div>
          </div>

          <div className="stat-col">
            <div className="stats-card" style={{ borderLeft: '4px solid #FF49CD' }}>
              <div className="stat-content">
                <div className="stat-info">
                  <p className="stat-title">Pending Bookings</p>
                  <h2 className="stat-value">{statistics.PendingBookings}</h2>
                </div>
                <div className="stat-icon">
                  <img src={activeDriverIcon} alt="Pending Bookings" />
                </div>
              </div>
            </div>
          </div>

          <div className="stat-col">
            <div className="stats-card" style={{ borderLeft: '4px solid #FDAF22' }}>
              <div className="stat-content">
                <div className="stat-info">
                  <p className="stat-title">Completed</p>
                  <h2 className="stat-value">{statistics.CompletedBookings}</h2>
                </div>
                <div className="stat-icon">
                  <img src={completedJobsIcon} alt="Completed" />
                </div>
              </div>
            </div>
          </div>

          <div className="stat-col">
            <div className="stats-card" style={{ borderLeft: '4px solid #32D484' }}>
              <div className="stat-content">
                <div className="stat-info">
                  <p className="stat-title">Cancelled Bookings</p>
                  <h2 className="stat-value">{statistics.CancelledBookings}</h2>
                </div>
                <div className="stat-icon">
                  <img src={stopSignIcon} alt="Cancelled Bookings" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs + Sort */}
        <div className="controls-row">
          <div className="filter-tabs" role="tablist" aria-label="Booking filters">
            {["All", "New", "Completed", "Ongoing", "Cancelled"].map((tab) => (
              <button
                key={tab}
                className={`tab-btn booking_tab_fil ${filter === tab ? "active" : ""}`}
                onClick={() => setFilter(tab)}
                role="tab"
                aria-selected={filter === tab}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="sort-wrapper">
            <label className="sort-label">Sort By</label>
            <select
              className="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="Sort bookings"
            >
              <option>Newest</option>
              <option>Oldest</option>
              <option>Amount (Low)</option>
              <option>Amount (High)</option>
            </select>
          </div>
        </div>

        {/* Bookings Table Wrapper */}
        <div className="bookings-table-wrapper">
          {/* Header Row */}
          <div className="list-header">
            <div className="col col-id">Booking ID</div>
            <div className="col col-service">Service Request</div>
            <div className="col col-amount">Amount</div>
            <div className="col col-status">Status</div>
            <div className="col col-action">Action</div>
          </div>

          {/* Loading State - Shimmer */}
          {loading && (
          <div className="bookings-list">
            {[1, 2, 3, 4].map((item) => (
              <div className="booking-card" key={item}>
                <div className="booking-grid">
                  <div className="col col-id">
                    <ShimmerBox width="80px" height="16px" />
                    <ShimmerBox width="100px" height="14px" />
                  </div>
                  <div className="col col-service">
                    <ShimmerBox width="120px" height="16px" />
                    <ShimmerBox width="90px" height="14px" />
                  </div>
                  <div className="col col-amount">
                    <ShimmerBox width="60px" height="18px" />
                  </div>
                  <div className="col col-status">
                    <ShimmerBox width="80px" height="28px" borderRadius="4px" />
                  </div>
                  <div className="col col-action">
                    <ShimmerBox width="100px" height="36px" borderRadius="8px" />
                  </div>
                </div>
                <div className="booking-meta">
                  <div className="meta-left">
                    <ShimmerBox width="200px" height="14px" />
                    <ShimmerBox width="150px" height="14px" />
                  </div>
                  <div className="meta-right">
                    <ShimmerBox width="100px" height="14px" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-state">
            <div className="error-state-icon">⚠️</div>
            <h3 className="error-state-title">Something Went Wrong</h3>
            <p className="error-state-text">{error}</p>
            <button className="error-state-button" onClick={fetchBookings}>Try Again</button>
          </div>
        )}

        {/* Booking Cards */}
        {!loading && !error && (
          <div className="bookings-list">
            {bookings.length > 0 ? (
              bookings.map((b) => (
                <div className="booking-card" key={b.BookingId}>
                  <div className="booking-grid">
                    <div className="col col-id">
                      <div className="booking-id">{b.BookingNumber}</div>
                      <div className="booking-name">{b.CustomerName}</div>
                    </div>

                    <div className="col col-service">
                      <div className="service-title booking_service">{b.ServiceName}</div>
                      <div className="service-date">{b.ServiceDate}</div>
                    </div>

                    <div className="col col-amount">
                      <div className="price">{formatAmount(b.Amount)}</div>
                    </div>

                    <div className="col col-status">
                      <span className={`booking-status ${statusClass[b.Status] || 'status-new'}`}>
                        {b.Status}
                      </span>
                    </div>

                    <div className="col col-action">
                      <button
                        className="view-btn"
                        onClick={() => handleViewDetails(b)}
                        aria-label={`View details for booking ${b.BookingNumber}`}
                      >
                        View Details
                      </button>
                    </div>
                  </div>

                  <div className="booking-meta">
                    <div className="meta-left">
                      <span className="meta-item">
                        <img src={addressIcon} alt="" className="meta-icon" />
                        <span>Delivery: {b.DeliveryLocation || b.FullAddress || 'N/A'}</span>
                      </span>
                      <span className="meta-divider">|</span>
                      <span className="meta-item">
                        <img src={scheduleIcon} alt="" className="meta-icon" />
                        <span>{b.Status === "Completed" ? "Delivered On:" : "Scheduled On:"} {b.ServiceDate}</span>
                      </span>
                       <span className="meta-divider">|</span>
                      <span className="meta-item-base">
                        <span>{b.BaseName}</span>
                      </span>
                    </div>

                    <div className="meta-right">
                      <span className="rating-label">Rating:</span>
                      {renderRating(b.Rating)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-bookings">
                <img src={bookingsEmptyIcon} alt="No Bookings" className="empty-state-icon" />
                <h3 className="empty-state-title">No Bookings Found</h3>
                <p className="empty-state-text">
                  {filter !== "All"
                    ? `There are no ${filter.toLowerCase()} bookings at the moment.`
                    : "You don't have any bookings yet. New bookings will appear here."
                  }
                </p>
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.TotalPages > 1 && (
              <div className="bookings-pagination">
                <button
                  className="pagination-btn"
                  disabled={!pagination.HasPrevious}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.CurrentPage} of {pagination.TotalPages}
                </span>
                <button
                  className="pagination-btn"
                  disabled={!pagination.HasNext}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
        </div>

      </div>
    </div>
  );
};

export default Bookings;
